import React, { FC, useMemo, useRef, useState } from "react";
import { Grid } from "@getregistered/greg-editors";
import { Box, Text } from "@primer/react";
import { Banner } from "@primer/react/experimental";
import { bulkDeleteBlockedDates, isRefusal } from "../BulkActions/bulkBlockedDateDeleteFlow";
import { BaseDialog } from "@getregistered/greg-editors/dist/lib/primer-ui/BaseDialog/BaseDialog";
import {
  gridLoader,
  markClearedDefaults,
  nestedFilterParams,
  NestedFilterParams,
} from "../../domain/grid/resultLoader";
import {
  BulkActionLink,
  DownloadLink,
  PaginatedApplicationPrimerGrid,
  SelectionStatePayload,
} from "./PaginatedApplicationPrimerGrid";
import { DEFAULT_PER_PAGE, DEFAULT_PER_PAGE_STORAGE_KEY } from "./constants";
import "../BulkActions/BulkActionDialogOverrides.css";

interface GridWithBulkActions extends Grid {
  bulkActions?: { key: string; label: string; dangerous?: boolean }[];
}

export interface BlockedDatesGridProps {
  grid: GridWithBulkActions;
  resultsUrl: string;
  bulkDeleteUrl: string;
  // greg-editors hangs the bulk action menu off the same toolbar menu the
  // download links populate, and renders neither when that menu has no links.
  // The CSV download is what puts the menu on screen for the bulk delete.
  downloadLinks?: DownloadLink[];
  showActions?: boolean;
}

/**
 * GR-3341: the blocked dates grid on Event Request Settings.
 *
 * Blocked dates are organisation settings rows, not event records, so they sit
 * outside the BulkOperation pipeline the event grids use (that pipeline is
 * scoped to an event and runs async through a job). Clearing out stale dates is
 * a single small DELETE, so this grid posts the selection straight to the
 * settings controller and reloads.
 */
export const BlockedDatesGrid: FC<BlockedDatesGridProps> = ({
  grid,
  resultsUrl,
  bulkDeleteUrl,
  downloadLinks,
  showActions = true,
}) => {
  const [selectionState, setSelectionState] = useState<SelectionStatePayload | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  // The filters the grid last fetched with, in the nested shape the server
  // parses. SelectionStatePayload.filterParams is the flat "f[col][]" form,
  // which applies no filter at all server-side (GR-2206), so a "select all"
  // built from it would delete the whole organisation's rows.
  const activeFilterParamsRef = useRef<NestedFilterParams>({});

  const selectionCount = selectionState
    ? selectionState.mode === "explicit"
      ? selectionState.selectedIds.length
      : selectionState.totalCount - selectionState.excludedIds.length
    : 0;

  // GR-3701: the grid mounts with Status defaulted to Upcoming (grid.filters).
  // A default the user clears has to go out as an explicit blank, or the
  // server applies it again; that goes for the rows fetch, the "select all"
  // the bulk delete resolves, and the CSV download alike.
  const loader = useMemo(() => {
    const base = gridLoader(resultsUrl, undefined, grid.columns);
    return {
      ...base,
      loadRecords: (options: Parameters<typeof base.loadRecords>[0], signal: AbortSignal) => {
        const filters = markClearedDefaults(grid.columns, grid.filters, options.filters);
        activeFilterParamsRef.current = nestedFilterParams(grid.columns, filters);
        return base.loadRecords({ ...options, filters }, signal);
      },
    };
  }, [resultsUrl, grid.columns, grid.filters]);

  const downloadLinksWithDefaults = useMemo(
    () => downloadLinks?.map((link) => ({ ...link, defaultFilters: grid.filters })),
    [downloadLinks, grid.filters]
  );

  const hasDelete = !!grid.bulkActions?.some((action) => action.key === "delete");

  const bulkActionLinks: BulkActionLink[] | undefined = useMemo(() => {
    if (!hasDelete) return undefined;

    return [
      {
        title: "Delete",
        variant: "danger",
        onClick: (state) => {
          setSelectionState(state);
          setErrors([]);
          setIsDeleteOpen(true);
        },
      },
    ];
  }, [hasDelete]);

  // A refusal keeps the dialog open carrying the server's reason. Reloading on
  // every outcome, as this did, reads as "it worked" for a request the server
  // turned down because it could not reproduce the selection.
  const handleDelete = async () => {
    if (!selectionState || isDeleting) return;
    setIsDeleting(true);
    setErrors([]);

    const result = await bulkDeleteBlockedDates(
      bulkDeleteUrl,
      selectionState,
      activeFilterParamsRef.current
    );

    if (isRefusal(result)) {
      setErrors(result.errors);
      setIsDeleting(false);
      return;
    }

    window.location.reload();
  };

  const closeDelete = () => {
    setIsDeleteOpen(false);
    setErrors([]);
  };

  return (
    <>
      <PaginatedApplicationPrimerGrid
        grid={grid}
        loader={loader}
        defaultPerPage={DEFAULT_PER_PAGE}
        perPageStorageKey={DEFAULT_PER_PAGE_STORAGE_KEY}
        page={1}
        actionCellRenderer="menu"
        downloadLinks={downloadLinksWithDefaults}
        showActions={showActions}
        enableRowSelection={hasDelete}
        bulkActionLinks={bulkActionLinks}
      />

      {/* BaseDialog rather than greg-editors' DeleteModal: that modal hardcodes
          "Delete Registrations" in its title and confirm button, which is the
          wrong noun here. */}
      <BaseDialog
        isOpen={isDeleteOpen}
        onClose={closeDelete}
        title="Remove blocked dates"
        position="center"
        width="600px"
        leftButtonText={errors.length > 0 ? "Close" : "Go back"}
        rightButtonText={
          selectionCount === 1
            ? "Yes, remove 1 blocked date"
            : `Yes, remove ${selectionCount} blocked dates`
        }
        rightButtonVariant="danger"
        onLeftButtonClick={closeDelete}
        onRightButtonClick={handleDelete}
        rightButtonDisabled={isDeleting || selectionCount === 0}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {errors.length > 0 && (
            <Banner variant="critical" title={errors.join(" ")} />
          )}
          <Text as="div" fontSize={1}>
            {selectionCount === 1
              ? "This blocked date will be removed and the day becomes bookable again."
              : `These ${selectionCount} blocked dates will be removed and those days become bookable again.`}
          </Text>
          <Banner
            variant="critical"
            title="Existing bookings are not changed. This cannot be undone."
          />
        </Box>
      </BaseDialog>
    </>
  );
};
